import { useEffect, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { useTwoFactorAuth } from '../../hooks/useTwoFactorAuth';
import RecoveryCodes from './RecoveryCodes';
import { useRecoveryCodes } from '../../hooks/useRecoveryCodes';

const TwoFactorAuth = ({ email }: { email: string }) => {
  const {
    qrGenerated,
    twoFactorEnabled,
    otpAuthUrl,
    isValid,
    loading,
    checkingStatus,
    error,
    generateSecret,
    verifyCode,
    disable2FA,
    check2FAStatus,
  } = useTwoFactorAuth();

  const { handleResetCodes, handleResetShowCodes } = useRecoveryCodes();

  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  // ✅ Verificar el estado inicial de 2FA al cargar el componente
  useEffect(() => {
    check2FAStatus(email);
  }, []);

  // ✅ Activar Google Authenticator y generar el QR Code
  const handleEnable2FA = async () => {
    await generateSecret(email);
  };

  // ✅ Verificar el código ingresado por el usuario
  const handleVerify2FA = async () => {
    setVerifying(true);
    try {
      await verifyCode(email, verificationCode);
    } catch (err) {
      console.error('Error al verificar código:', err);
    } finally {
      setVerifying(false);
    }
  };

  // ✅ Desactivar 2FA
  const handleDisable2FA = async () => {
    await disable2FA(email);
    handleResetShowCodes();
    setVerificationCode('');
    handleResetCodes();
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-black dark:text-white">
        Habilitar la autenticación de dos factores (2FA)
      </h2>

      <p className="text-gray-600 mt-2">
        Las aplicaciones de autenticación generan contraseñas de un solo uso que
        se utilizan como un segundo factor para verificar su identidad cuando se
        le solicita durante el inicio de sessión.
      </p>

      <h2 className="mt-2 font-semibold text-black dark:text-white">
        Escanee el código QR
      </h2>
      <p className="text-gray-600">
        Utilize una aplicación de autenticación para escanear.
      </p>

      <ul className="text-gray-600 mt-2 list-disc pl-5">
        <li>Google Authenticator</li>
        <li>Microsoft Authenticator</li>
        <li>Authy</li>
        <li>1Password</li>
        <li>Duo Mobile</li>
      </ul>

      {/* ✅ Botón para habilitar 2FA */}
      {!twoFactorEnabled ? (
        <button
          className="mt-4 rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90"
          onClick={handleEnable2FA}
          disabled={loading || checkingStatus}
        >
          {loading ? 'Generando...' : 'Habilitar Autenticador'}
        </button>
      ) : null}

      {/* ✅ Mostrar QR para escaneo */}
      {qrGenerated && otpAuthUrl && (
        <div className="mt-4 flex flex-col items-center">
          <div className="bg-white dark:bg-white rounded-lg border border-stroke dark:border-strokedark p-4">
            <QRCodeCanvas value={otpAuthUrl} size={200} />
          </div>
          <p className="text-gray-700 mt-2 dark:text-white">
            Escanea este código en tu aplicación de autenticación.
          </p>
        </div>
      )}

      {/* ✅ Input para ingresar código de verificación */}
      {qrGenerated && (
        <div className="mt-5 flex flex-col gap-5.5 sm:flex-row">
          <input
            type="text"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="Ingresa el código"
            className="w-full rounded-lg border-[1.5px] border-stroke bg-transparent py-3 pl-5 pr-4.5 text-black outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
            maxLength={6}
            disabled={verifying}
          />
          <button
            className={`w-full rounded py-3 text-white ${
              verifying
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-500 hover:bg-opacity-90'
            }`}
            onClick={handleVerify2FA}
            disabled={verifying}
          >
            {verifying ? 'Verificando...' : 'Verificar Código'}
          </button>
        </div>
      )}

      {/* ✅ Botón para desactivar 2FA */}
      {twoFactorEnabled && (
        <button
          className="mt-4 w-full rounded bg-meta-1 px-4 py-2 text-white hover:bg-opacity-90"
          onClick={handleDisable2FA}
          disabled={loading}
        >
          Desactivar 2FA
        </button>
      )}

      {/* ✅ Mensajes de éxito y error */}
      {isValid && (
        <p className="mt-2 text-green-500">✅ Código válido. 2FA activado.</p>
      )}
      {error && <p className="mt-2 text-red">{error}</p>}

      {/* Tu lógica de autenticación aquí */}
      <RecoveryCodes />
    </div>
  );
};

export default TwoFactorAuth;
